/**
 * Kirish sahifasi — PP-1211. PDP Design System uslubida.
 *
 * KO'RINISH: butun ekran hero gradient (120deg #1B56DB→#1445C4→#2748B6 —
 * DS dagi ikkita ruxsat etilgan gradientdan biri), o'ng-pastda katta oq 7%
 * PulseMark dekor, markazda oq karta (radius-24, shadow-modal).
 *
 * XAVFSIZLIK QOIDALARI:
 *   1. Xato matni HECH QACHON «bunday email yo'q» yoki «parol noto'g'ri»
 *      deb ajratmaydi — u faqat «Email yoki parol noto'g'ri». Ajratilgan
 *      xabar hujumchiga qaysi emaillar mavjudligini aytadi. Matn serverdan
 *      keladi va bu yerda o'zgartirilmaydi.
 *   2. Parol maydoni `autocomplete="current-password"` bilan — brauzer parol
 *      menejeri to'g'ri ishlashi uchun (foydalanuvchi kuchli parol qo'yadi).
 *   3. Bloklangan hisob (5 ta xato urinishdan keyin) alohida matn oladi —
 *      bu ma'lumot sizishi emas: foydalanuvchi NIMA UCHUN kira olmayotganini
 *      bilmasa, u qayta-qayta urinib blokni uzaytiradi.
 */

import { useState } from "react";
import type { FormEvent, JSX } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, KeyRound, Lock, Mail } from "lucide-react";

import { ApiError, api, mfaApi, setTokens } from "../../api/client";
import { PulseMark, SpinnerIcon } from "../../ui/icons";
import { Button, ErrorPanel, Field, Input, cx } from "../../ui/primitives";

/** Input ichidagi chap prefiks ikon — DS lucide 1.85, muted. */
function FieldIcon({ icon: IconComponent }: { icon: typeof Mail }): JSX.Element {
  return (
    <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-muted">
      <IconComponent className="size-[18px]" strokeWidth={1.85} aria-hidden="true" />
    </span>
  );
}

/**
 * Karta ostidagi «puls» dekori — brend belgisi metaforasi: besh ustun
 * navbat bilan pulslanadi (brand200, urish ustuni — sariq).
 * `prefers-reduced-motion` da animatsiya `index.css` orqali o'chadi.
 */
function PulseBars(): JSX.Element {
  const bars = [
    { height: 14, color: "bg-brand-200", delay: "0s" },
    { height: 26, color: "bg-brand-200", delay: "0.2s" },
    { height: 38, color: "bg-yellow", delay: "0.4s" },
    { height: 26, color: "bg-brand-200", delay: "0.6s" },
    { height: 14, color: "bg-brand-200", delay: "0.8s" },
  ];

  return (
    <div className="mt-6 flex h-10 items-end justify-center gap-1.5" aria-hidden="true">
      {bars.map((bar, index) => (
        <span
          key={index}
          className={cx("pulse-bar w-1.5 rounded-full", bar.color)}
          style={{ height: bar.height, animationDelay: bar.delay }}
        />
      ))}
    </div>
  );
}

export default function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Ikkinchi omil bosqichi (PP-1212).
   *
   * `null` — parol bosqichi. Satr — server bergan oraliq token va endi
   * kod so'raladi. Parol STATE DA SAQLANMAYDI: u ikkinchi bosqichda
   * kerak emas, saqlansa esa xotirada ortiqcha turadi.
   */
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState("");
  /** Parolni ko'rsatish — foydalanuvchi xatosini kamaytiradi. */
  const [showPassword, setShowPassword] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const result = await api.login(email.trim(), password);

      if (result.mfaRequired === true) {
        setChallengeToken(result.challengeToken);
        setPassword("");
        return;
      }

      setTokens(result.accessToken, result.refreshToken);
      navigate("/");
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, "INTERNAL", "Serverga ulanib bo'lmadi. Internet aloqasini tekshiring."),
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: FormEvent): Promise<void> {
    event.preventDefault();

    if (challengeToken === null) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const result = await mfaApi.challenge(challengeToken, code.trim());
      setTokens(result.accessToken, result.refreshToken);
      navigate("/");
    } catch (caught) {
      const failure =
        caught instanceof ApiError
          ? caught
          : new ApiError(0, "INTERNAL", "Serverga ulanib bo'lmadi");

      // Oraliq token muddati 5 daqiqa. Tugagach kod kiritish foydasiz —
      // foydalanuvchi parol bosqichiga qaytariladi, aks holda u tugamaydigan
      // «kod noto'g'ri» tsikliga tushadi.
      if (failure.status === 401) {
        setChallengeToken(null);
        setCode("");
      }

      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-hero px-4 py-10">
      {/* Nozik grid-pattern — oq 4% chiziqlar (hero naqshi uslubida). */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />

      {/* Dekor — o'ngda ekran balandligicha 7% shaffof OQ brend belgisi. */}
      <svg
        viewBox="0 0 200 200"
        className="pointer-events-none absolute -right-[12vh] top-1/2 h-[100vh] w-[100vh] -translate-y-1/2 text-white opacity-[0.07]"
        aria-hidden="true"
      >
        <g fill="currentColor">
          <rect x="26.5" y="76" width="23" height="48" rx="6" />
          <rect x="66.5" y="15" width="23" height="170" rx="6" />
          <rect x="106.5" y="42" width="23" height="116" rx="6" />
          <rect x="146.5" y="64" width="23" height="72" rx="6" />
          <rect x="186.5" y="78" width="23" height="44" rx="6" />
        </g>
      </svg>

      {/* Yumshoq oq dog'lar — dekorativ chuqurlik (5–8% oq, blur). */}
      <div
        className="pointer-events-none absolute -left-32 -top-32 size-[420px] rounded-full bg-white/[0.06] blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-40 left-1/4 size-[360px] rounded-full bg-white/[0.05] blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute right-1/4 top-10 size-[280px] rounded-full bg-white/[0.08] blur-3xl"
        aria-hidden="true"
      />

      <div className="relative w-full max-w-md">
        {/* Karta ustidagi brend lokapi + tagline. */}
        <div className="mb-7 text-center">
          <div className="flex items-center justify-center gap-3">
            <PulseMark className="size-10" />
            <span className="text-[22px] font-extrabold tracking-[-0.02em] text-white">
              PDP Pulse
            </span>
          </div>
          <p className="mt-1.5 text-[15px] font-semibold text-white/[0.78]">
            Savdo jamoangizning pulsi
          </p>
        </div>

        {/* Oq karta — radius-24, shadow-modal, RAMKASIZ. */}
        <div className="rounded-panel bg-surface p-8 shadow-modal sm:p-10">
          <h1 className="text-[34px] font-black leading-tight tracking-[-0.04em] text-ink sm:text-[40px]">
            {challengeToken === null ? "Xush kelibsiz" : "Tasdiqlash kodi"}
          </h1>
          <p className="mt-2 text-[15px] text-body">
            {challengeToken === null
              ? "Hisobingiz ma'lumotlarini kiriting."
              : "Autentifikator ilovasidagi 6 raqamli kodni kiriting."}
          </p>

          {challengeToken !== null ? (
            <form onSubmit={(event) => void submitCode(event)} className="mt-8 space-y-5">
              <Field
                label="Kod"
                htmlFor="code"
                hint="Telefonni yo'qotgan bo'lsangiz — tiklash kodini kiriting (XXXXX-XXXXX)."
              >
                <div className="relative">
                  <FieldIcon icon={KeyRound} />
                  <Input
                    id="code"
                    // `text`, `number` emas: `number` maydonida boshidagi nol
                    // yo'qoladi va tiklash kodi umuman kiritilmaydi.
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    autoFocus
                    placeholder="123456"
                    className="pl-11 text-center text-lg tracking-[0.3em]"
                    value={code}
                    onChange={(event) => {
                      setCode(event.target.value);
                    }}
                  />
                </div>
              </Field>

              {error ? <ErrorPanel message={error.message} traceId={error.traceId} /> : null}

              <Button type="submit" variant="primary" size="lg" disabled={busy} className="w-full">
                {busy ? (
                  <>
                    <SpinnerIcon className="size-4" />
                    Tekshirilmoqda…
                  </>
                ) : (
                  "Tasdiqlash"
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="lg"
                className="w-full"
                onClick={() => {
                  setChallengeToken(null);
                  setCode("");
                  setError(null);
                }}
              >
                Orqaga
              </Button>
            </form>
          ) : (
            <form onSubmit={(event) => void submit(event)} className="mt-8 space-y-5">
              <Field label="Email" htmlFor="email">
                <div className="relative">
                  <FieldIcon icon={Mail} />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    autoFocus
                    placeholder="ism@kompaniya.uz"
                    className="pl-11"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                    }}
                  />
                </div>
              </Field>

              <Field label="Parol" htmlFor="password">
                <div className="relative">
                  <FieldIcon icon={Lock} />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                    className="pl-11 pr-12"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                    }}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Parolni yashirish" : "Parolni ko'rsatish"}
                    aria-pressed={showPassword}
                    onClick={() => {
                      setShowPassword((previous) => !previous);
                    }}
                    className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-tile text-muted transition-colors hover:text-brand-600 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-600"
                  >
                    {showPassword ? (
                      <EyeOff className="size-[18px]" strokeWidth={1.85} aria-hidden="true" />
                    ) : (
                      <Eye className="size-[18px]" strokeWidth={1.85} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </Field>

              {error ? <ErrorPanel message={error.message} traceId={error.traceId} /> : null}

              <Button type="submit" variant="primary" size="xl" disabled={busy} className="w-full">
                {busy ? (
                  <>
                    <SpinnerIcon className="size-4" />
                    Tekshirilmoqda…
                  </>
                ) : (
                  "Kirish"
                )}
              </Button>
            </form>
          )}

          <p className="mt-6 text-[13px] font-semibold leading-relaxed text-tertiary">
            Parolni unutdingizmi? Kompaniyangiz rahbariga murojaat qiling — parolni tiklash havolasi
            elektron pochtangizga yuboriladi.
          </p>
        </div>

        {/* Karta ostidagi puls dekori. */}
        <PulseBars />

        <div className="mt-5 text-center text-[13px] font-semibold text-white/60">
          pulse.pdp.uz · PDP EcoSystem
        </div>
      </div>
    </div>
  );
}
